import React, { RefObject, memo, useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { runOnJS, useAnimatedProps, useAnimatedReaction } from 'react-native-reanimated';
import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context';

import { useRoute } from '@react-navigation/native';

import { Box } from '@/components/lib/components';
import { Handle, MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { useFantasyFullScreenPagerTransition } from '@/feature/circular-pager/FantasyFullScreenPagerTransitionProvider';
import { ScrollableModalContextProvider } from '@/feature/circular-pager/ScrollableModalContextProvider';
import { CircularPagerImperativeApi } from '@/feature/circular-pager/types';
import { PlayerProfileSheetScreen } from '@/feature/player-profile-sheets/components/PlayerProfileSheetScreen';
import { PlayerProfileTitle } from '@/feature/player-profile-sheets/components/PlayerProfileTitle';
import { usePlayerProfileDataStore } from '@/feature/player-profile-sheets/hooks/use-player-profile-data-store';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { isPGA } from '@/utils/league';
import BottomSheet, { BottomSheetBackdropProps } from '@gorhom/bottom-sheet';

import { FantasyEventsSheetsParamsList } from '../FantasyEventsSheets';
import { useSinglePlayerProfile } from '../SinglePlayerProfileProvider';
import { EVENT_SHEET_TITLE_HEIGHT } from './EventSheetTitle';
import { EVENT_CAROUSEL_HEIGHT, PGA_EVENT_CAROUSEL_HEIGHT } from './EventsCarouselTabBar';

const ONLY_ONE_EVENT_HEIGHT = 4;

export const SinglePlayerProfileModal = memo(
    ({
        onlyOnePage,
        circularPagerRef,
    }: {
        onlyOnePage: boolean;
        circularPagerRef: RefObject<CircularPagerImperativeApi>;
    }) => {
        const { height } = useSafeAreaFrame();
        const { playerProfileModalRef, playerProfileAnimatedIndex, isPlayerProfileModalOpen } =
            useSinglePlayerProfile();
        const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();
        const insets = useSafeAreaInsets();
        const [animatedIndexValue, setAnimatedIndexValue] = useState(0);

        const onClosePlayerProfileModal = useCallback(() => {
            isPlayerProfileModalOpen.value = false;
            playerProfileAnimatedIndex.value = -1;
            circularPagerRef.current?.setInteractionsEnabled(true);
        }, [circularPagerRef, isPlayerProfileModalOpen, playerProfileAnimatedIndex]);

        const closeModal = useCallback(() => {
            playerProfileModalRef.current?.close();
        }, [playerProfileModalRef]);
        const { animatedIndex } = useFantasyFullScreenPagerTransition();
        useAnimatedReaction(
            () => animatedIndex.value,
            value => {
                if (Number.isInteger(value)) {
                    runOnJS(setAnimatedIndexValue)(value);
                }
            },
            [animatedIndex]
        );

        const topContentHeight = useMemo(() => {
            if (onlyOnePage || animatedIndexValue > 0) {
                return ONLY_ONE_EVENT_HEIGHT;
            }
            if (isPGA(params.league)) {
                return PGA_EVENT_CAROUSEL_HEIGHT;
            }
            return EVENT_CAROUSEL_HEIGHT;
        }, [animatedIndexValue, onlyOnePage, params.league]);

        return (
            <ScrollableModalContextProvider>
                <BottomSheet
                    index={-1}
                    snapPoints={[height - topContentHeight - insets.top - 12, height - insets.top - 16]}
                    enablePanDownToClose
                    enableDynamicSizing={false}
                    animatedIndex={playerProfileAnimatedIndex}
                    ref={playerProfileModalRef}
                    style={styles.bottomSheet}
                    onClose={onClosePlayerProfileModal}
                    onChange={index => {
                        const hasOpened = index !== -1;
                        if (hasOpened) {
                            circularPagerRef.current?.setInteractionsEnabled(false);
                        }
                    }}
                    backgroundStyle={styles.bottomSheetBackground}
                    backdropComponent={CustomBackdrop}
                    handleStyle={styles.hide}
                >
                    <ModalContent closeModal={closeModal} />
                </BottomSheet>
            </ScrollableModalContextProvider>
        );
    }
);

// used to block behind the bottom sheet when the bottom sheet is open
const CustomBackdrop = ({ animatedIndex }: BottomSheetBackdropProps) => {
    const animatedProps = useAnimatedProps(() => {
        const pointerEvents: 'auto' | 'box-none' = animatedIndex.value > -1 ? 'auto' : 'box-none';

        return {
            pointerEvents,
        };
    });

    return <Animated.View animatedProps={animatedProps} style={common.flex} />;
};

const ModalContent = memo(({ closeModal }: { closeModal: () => void }) => {
    const selectedSinglePlayer = usePlayerProfileDataStore(state => state.selectedSinglePlayer);
    return selectedSinglePlayer ? (
        <>
            <Box style={styles.outerBorderWrapper} />
            <Box style={styles.innerBackgroundOverlay} />

            <Box
                position={'absolute'}
                zIndex={2}
                width={'100%'}
                height={EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT}
                overflow={'hidden'}
                borderTopLeftRadius={'r20'}
                borderTopRightRadius={'r20'}
            >
                <Animated.View>
                    <Handle
                        handleColor={withOpacity(designSystem.colors.white, 0.15)}
                        handleContainerStyle={common.backgroundColorTransparent}
                    />
                    <PlayerProfileTitle
                        fromSinglePlayerProfile
                        playerInfo={selectedSinglePlayer}
                        onClose={closeModal}
                    />
                </Animated.View>
            </Box>
            <PlayerProfileSheetScreen
                playerInfo={selectedSinglePlayer}
                closeSinglePlayerProfile={closeModal}
                isActive={true}
            />
        </>
    ) : null;
});

const OUTER_RADIUS = 20;
const INNER_RADIUS = 19; // slightly smaller to reveal border arc
const BORDER_HEIGHT = 20;
const styles = StyleSheet.create({
    bottomSheetBackground: {
        backgroundColor: designSystem.colors.gray8,
        borderCurve: 'continuous',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
    },
    bottomSheet: {
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        borderCurve: 'continuous',
        overflow: 'hidden',
    },
    transformOriginTop: {
        transformOrigin: 'top',
    },
    hide: {
        display: 'none',
    },
    outerBorderWrapper: {
        backgroundColor: withOpacity(designSystem.colors.white, 0.05),
        borderTopLeftRadius: OUTER_RADIUS,
        borderTopRightRadius: OUTER_RADIUS,
        height: BORDER_HEIGHT,
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    innerBackgroundOverlay: {
        backgroundColor: designSystem.colors.gray8,
        borderTopLeftRadius: INNER_RADIUS,
        borderTopRightRadius: INNER_RADIUS,
        height: BORDER_HEIGHT,
        marginTop: 1,
        width: '100%',
        position: 'absolute',
        left: 0,
    },
});
